import Button from '@carvana/ds-button';
import { TriangleAlert } from 'lucide-react';
import useModal from '@carvana/ds-modal';
import { icon } from '@carvana/ds-styles';
import React, { useCallback, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rollBack } from '|/app/services';
import { FormStateContext } from '|/context';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { clearScopedSlotPreference } from '|/hooks/use-slot-preference';
import useSubmit from '|/hooks/use-submit';
import styles from './edit-order-modal.module.css';

interface EditOrderModalDSProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryPreference?: string | null;
  editingSectionName?: string;
  targetStep?: string;
  currentSectionName?: string;
}

const EditOrderModal = ({
  isOpen,
  onClose,
  deliveryPreference = null,
  editingSectionName = '',
  targetStep,
  currentSectionName
}: EditOrderModalDSProps) => {
  const { trackEvent } = useAnalyticsContext();
  const { isSubmitting } = useContext(FormStateContext);
  const submit = useSubmit();
  const { Modal, closeModal, openModal } = useModal();
  const navigate = useNavigate();

  const [, , , route] = window.location.pathname.split('/');

  const deliveryOrPickup = deliveryPreference?.toLowerCase().includes('delivery')
    ? 'delivery'
    : 'pickup';
  const isOrderPlaced = route === 'order-placed';

  useEffect(() => {
    if (isOpen) {
      openModal();
    } else {
      closeModal();
    }
  }, [isOpen, openModal, closeModal]);

  const handleConfirmEdit = useCallback(() => {
    if (isOrderPlaced) {
      trackEvent('v2_opd_edit_order_button_tap');
    }

    if (currentSectionName === 'Finalize Purchase') {
      trackEvent('v2_finalize_purchase_edit_button_clicked');
    }

    const abortController = new AbortController();

    submit(
      rollBack({
        cancelTransfer: true
      }).then(() => {
        clearScopedSlotPreference();
        if (targetStep) {
          const returnUrl = encodeURIComponent(`/steps/${targetStep}`);
          navigate(`/next-step?returnUrl=${returnUrl}`, { replace: true });
          return;
        }

        navigate('/purchase', { replace: true });
      }),
      abortController.signal
    );
  }, [submit, trackEvent, currentSectionName, targetStep, navigate, isOrderPlaced]);

  const handleModalClose = useCallback(() => {
    trackEvent('pp_edit_order_modal_closed');
    onClose();
  }, [onClose, trackEvent]);

  const handleGoBack = useCallback(() => {
    trackEvent('pp_edit_order_go_back');
    onClose();
  }, [onClose, trackEvent]);

  return (
    <Modal
      modalHeader="Edit order"
      onClose={handleModalClose}
      desktopWidth="480px"
      mobileStyle="full"
    >
      <div className={styles.content}>
        <div className={styles.alertSection}>
          <div className={styles.alertHeader}>
            <TriangleAlert className={styles.alertIcon} color={icon.warning} size={48} />
            <h3 className={styles.alertTitle}>
              {isOrderPlaced
                ? `This will release your ${deliveryOrPickup} date`
                : 'Are you sure you want to edit your order?'}
            </h3>
          </div>
          <p className={styles.alertDescription}>
            {isOrderPlaced ? (
              <>
                Editing your order will release your {deliveryOrPickup} appointment and{' '}
                <strong>
                  your vehicle will become available for others to purchase if you don&apos;t
                  schedule a new appointment before the timer expires
                </strong>
              </>
            ) : (
              'You will have a limited time to make your changes, otherwise your vehicle will become unlocked and available for others to purchase.'
            )}
          </p>
        </div>
      </div>

      <div className={styles.stickyButtonContainer}>
        <div className={styles.footer}>
          <Button
            type="button"
            variant="tertiary"
            size="md"
            onClick={handleConfirmEdit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            data-analytics-id="pp_edit_modal_edit"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {isOrderPlaced ? 'Release delivery date' : 'Yes, edit'}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={handleGoBack}
            disabled={isSubmitting}
            data-analytics-id="pp_edit_modal_cancel"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {isOrderPlaced ? 'Go back' : 'No, go back'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditOrderModal;
